import React, { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  ColumnDef,
} from "@tanstack/react-table";
import { MdEdit, MdDelete, MdFitnessCenter, MdPlayArrow } from "react-icons/md";
import { Exercise } from "../../types/exercise";
import Card from "../card";
import { getYouTubeThumbnailFromUrl } from "../../utils/youtube";
import TrainingMethodBadge from "../badges/TrainingMethodBadge";
import DataTable from "../common/DataTable";
import SearchForm from "../common/SearchForm";
import LoadingSpinner from "../common/LoadingSpinner";

interface ExercisesTableProps {
  exercises: Exercise[];
  loading: boolean;
  onEdit: (exercise: Exercise) => void;
  onDelete: (exercise: Exercise) => void;
  onView: (exercise: Exercise) => void;
  onSearch?: (searchTerm: string) => void;
  onClearSearch?: () => void;
  searchTerm?: string;
  isSearchMode?: boolean;
}

const columnHelper = createColumnHelper<Exercise>();

const ExercisesTable: React.FC<ExercisesTableProps> = ({
  exercises,
  loading,
  onEdit,
  onDelete,
  onView,
  onSearch,
  onClearSearch,
  searchTerm = "",
  isSearchMode = false,
}) => {
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  React.useEffect(() => {
    setLocalSearchTerm(searchTerm);
  }, [searchTerm]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch && localSearchTerm.trim()) {
      onSearch(localSearchTerm.trim());
    }
  };

  const handleClearSearch = () => {
    setLocalSearchTerm("");
    if (onClearSearch) {
      onClearSearch();
    }
  };
  const columns = useMemo<ColumnDef<Exercise, any>[]>(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        header: () => "Exercise Name",
        cell: (info) => {
          const exercise = info.row.original;
          const thumbnailUrl = exercise.yt_link
            ? getYouTubeThumbnailFromUrl(exercise.yt_link)
            : null;

          return (
            <div className="flex items-center">
              <div
                className="h-20 w-32 flex-shrink-0 cursor-pointer"
                onClick={() => onView(exercise)}
              >
                {thumbnailUrl ? (
                  <img
                    src={thumbnailUrl}
                    alt={`${exercise.name} thumbnail`}
                    className="h-20 w-32 rounded-lg object-cover"
                    onError={(e) => {
                      // Fallback to placeholder if thumbnail fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      target.nextElementSibling?.classList.remove("hidden");
                    }}
                  />
                ) : (
                  <div className="flex h-20 w-32 items-center justify-center rounded-lg bg-biafit-primary text-white">
                    <MdFitnessCenter className="h-10 w-10" />
                  </div>
                )}
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-navy-700 dark:text-white">
                  {exercise.name}
                </div>
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor("primary_muscle_groups", {
        header: () => "Muscle Groups",
        cell: (info) => {
          const exercise = info.row.original;
          const primaryMuscles = exercise.primary_muscle_groups || [];
          const secondaryMuscles = exercise.secondary_muscle_groups || [];

          if (primaryMuscles.length === 0 && secondaryMuscles.length === 0) {
            return (
              <div className="text-sm font-medium">
                <span className="italic text-gray-400">Not specified</span>
              </div>
            );
          }

          return (
            <div className="flex flex-wrap gap-1">
              {primaryMuscles.map((mg) => (
                <span
                  key={mg.id}
                  className="inline-flex items-center rounded-full bg-biafit-primary px-2 py-0.5 text-xs font-medium text-white"
                >
                  {mg.name}
                </span>
              ))}
              {secondaryMuscles.map((mg) => (
                <span
                  key={mg.id}
                  className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800 dark:bg-purple-900 dark:text-purple-300"
                >
                  {mg.name}
                </span>
              ))}
            </div>
          );
        },
      }),
      columnHelper.accessor("training_method", {
        header: () => "Type",
        cell: (info) => {
          const trainingMethod = info.getValue();
          return (
            <TrainingMethodBadge
              trainingMethod={trainingMethod}
              className="px-2.5"
            />
          );
        },
      }),
      columnHelper.accessor("yt_link", {
        header: () => "Video",
        cell: (info) => {
          const ytLink = info.getValue();
          return (
            <div className="text-sm">
              {ytLink ? (
                <a
                  href={ytLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-biafit-primary hover:text-biafit-secondary"
                  title="Watch on YouTube"
                >
                  <MdPlayArrow className="mr-1 h-4 w-4" />
                  Video
                </a>
              ) : (
                <span className="italic text-gray-400">No video</span>
              )}
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: () => "Actions",
        cell: (info) => {
          const exercise = info.row.original;
          return (
            <div className="flex space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(exercise);
                }}
                className="text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                title="Edit Exercise"
              >
                <MdEdit className="h-5 w-5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(exercise);
                }}
                className="text-gray-600 transition-colors hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                title="Delete Exercise"
              >
                <MdDelete className="h-5 w-5" />
              </button>
            </div>
          );
        },
      }),
    ],
    [onEdit, onDelete, onView]
  );

  const table = useReactTable({
    data: exercises,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (loading) {
    return (
      <Card extra="w-full h-full p-3 md:p-6 sm:overflow-x-auto">
        <LoadingSpinner />
      </Card>
    );
  }

  return (
    <Card extra="w-full h-full p-3 md:p-6 sm:overflow-x-auto">
      <div className="relative flex flex-col items-start justify-between space-y-4 pb-4 sm:flex-row sm:items-center sm:space-y-0">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          Exercise Library
        </div>

        <div className="w-full sm:w-auto">
          <SearchForm
            value={localSearchTerm}
            onChange={setLocalSearchTerm}
            onSubmit={handleSearchSubmit}
            onClear={handleClearSearch}
            placeholder="Search exercises..."
            hasSearched={isSearchMode}
            loading={loading}
          />
          {isSearchMode && (
            <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
              Showing search results for "{searchTerm}"
            </div>
          )}
        </div>
      </div>

      <DataTable
        table={table}
        tableKey={`exercises-table-${isSearchMode ? "search" : "regular"}`}
        isEmpty={exercises.length === 0}
        empty={{ icon: MdFitnessCenter, title: "No exercises found" }}
        onRowClick={(_event, row: Exercise) => onView(row)}
        mobile={exercises.map((exercise) => {
          const thumbnailUrl = exercise.yt_link
            ? getYouTubeThumbnailFromUrl(exercise.yt_link)
            : null;
          return (
            <div
              key={exercise.id}
              className="cursor-pointer rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-navy-900 md:p-4"
              onClick={() => onView(exercise)}
            >
              <div className="mb-3 flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="h-16 w-28 flex-shrink-0">
                    {thumbnailUrl ? (
                      <img
                        src={thumbnailUrl}
                        alt={`${exercise.name} thumbnail`}
                        className="h-16 w-28 rounded-lg object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = "none";
                          const sibling =
                            target.nextElementSibling as HTMLElement;
                          if (sibling) sibling.classList.remove("hidden");
                        }}
                      />
                    ) : null}
                    <div
                      className={`flex h-16 w-28 items-center justify-center rounded-lg bg-biafit-primary text-white ${
                        thumbnailUrl ? "hidden" : ""
                      }`}
                    >
                      <MdFitnessCenter className="h-8 w-8" />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="mb-1 text-sm font-medium leading-4 text-navy-700 dark:text-white">
                      {exercise.name}
                    </h3>
                    <div className="mt-1">
                      <TrainingMethodBadge
                        trainingMethod={exercise.training_method}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(exercise);
                    }}
                    className="p-1 text-gray-600 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                    title="Edit Exercise"
                  >
                    <MdEdit className="h-5 w-5" />
                  </button>
                  {/* <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onDelete(exercise)
                      }}
                      className="p-1 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      title="Delete Exercise"
                    >
                      <MdDelete className="h-5 w-5" />
                    </button> */}
                </div>
              </div>
              <div className="flex items-center gap-3 text-sm">
                {exercise.yt_link ? (
                  <a
                    href={exercise.yt_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-biafit-primary hover:text-biafit-secondary"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MdPlayArrow className="mr-1 h-4 w-4" />
                    Video
                  </a>
                ) : (
                  <span className="italic text-gray-400">No video</span>
                )}
              </div>
            </div>
          );
        })}
      />
    </Card>
  );
};

export default ExercisesTable;
